import { z } from 'zod';
import { DateSchema } from './entities/shared.schema';

/**
 * User subscription schema representing the data stored in the 'users' document.
 */
export const UserSubscriptionSchema = z.object({
  cancelAtPeriodEnd: z.boolean(),
  cancelAt: DateSchema.nullable().optional(),
  currentPeriodEnd: DateSchema,
  planId: z.string().optional(),
  planName: z.string().optional(),
  startDate: DateSchema,
  status: z.enum(['active', 'cancelled', 'past_due', 'trialing', 'expired']).default('active'),
  stripeStatus: z.string().optional()
    .describe('Raw Stripe subscription status used for billing lifecycle decisions'),
  billingInterval: z.enum(['month', 'year']).optional(),
  updatedAt: DateSchema,
  pendingPlanId: z.string().optional().describe('Scheduled plan ID for next cycle'),
  pendingPlanName: z.string().optional().describe('Scheduled plan name for next cycle'),
  pendingPlanEffectiveAt: DateSchema.optional()
    .describe('When the scheduled plan change takes effect'),
  pendingBillingInterval: z.enum(['month', 'year']).optional(),
  maxTokens: z.number().int().default(0).describe('Max AI tokens allowed per subscription period'),
  tokenUsageId: z.string().nullable().optional()
    .describe('Key used for the current subscription-period tokenUsage record'),
});

/**
 * A single token spend event.
 */
export const TokenUsageEntrySchema = z.object({
  timestamp: z.string().datetime(),
  flowName: z.string().describe('Agent or flow that consumed the tokens (e.g. "sceneWriter")'),
  model: z.string().describe('Model identifier (e.g. "@cf/meta/llama-3.3-70b-instruct-fp8-fast")'),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  totalTokens: z.number().int().describe('Raw total tokens from the API'),
  tokenWeight: z.number().describe('Cost multiplier applied based on model pricing'),
  weightedTokens: z.number().int().describe('totalTokens * tokenWeight (what actually counts)'),
});

/**
 * Monthly aggregation of token usage.
 * TODO: If entries array grows too large (>500), migrate to subcollection
 */
export const MonthlyTokenUsageSchema = z.object({
  entries: z.array(TokenUsageEntrySchema).default([]),
  totalTokensSpent: z.number().int().default(0).describe('Sum of weightedTokens across all entries'),
});

export const CollaboratedNovelSchema = z.object({
  ownerId: z.string(),
  novelId: z.string(),
  role: z.enum(['collaborator', 'reader', 'example']),
  novelTitle: z.string().default('Untitled Novel'),
});

export const PendingInvitationSchema = z.object({
  token: z.string(),
  novelId: z.string(),
  ownerId: z.string(),
  novelTitle: z.string(),
  role: z.enum(['collaborator', 'reader']),
  invitedEmail: z.string().email().optional(),
  invitedUid: z.string().optional(),
}).refine((data) => (data.invitedEmail !== undefined) !== (data.invitedUid !== undefined), {
  message: "Either invitedEmail or invitedUid must be provided, but not both",
  path: ["invitedEmail", "invitedUid"],
});

export const UserSchema = z.object({
  id: z.string(),
  accountStatus: z.enum(['active', 'pending_deletion', 'purging']).optional().default('active'),
  deletionScheduledFor: DateSchema.optional(),
  publicId: z.string().optional().describe('Backend-generated stable public author identifier.'),
  email: z.string().email(),
  contactEmail: z.string().email().optional(),
  displayName: z.string().optional(),
  hasActiveSubscription: z.boolean().default(false),
  role: z.enum(['user', 'admin']).default('user'),
  isAdmin: z.boolean().default(false),
  planId: z.string().optional(),
  planName: z.string().optional(),
  maxTokens: z.number().int().default(0)
    .describe('Max AI tokens allowed per subscription period'),
  tokenUsageId: z.string().nullable().optional()
    .describe('Key used for the current tokenUsage record'),
  flags: z.record(z.string(), z.boolean()).optional(),
  photoURL: z.string().optional(),
  stripeCustomerId: z.string().optional().describe('Stripe Customer ID for billing management'),
  subscription: z.record(z.string(), UserSubscriptionSchema)
    .and(z.object({ current: UserSubscriptionSchema })).optional(),
  hasSeenDashboardTour: z.boolean().default(false),
  tokenUsage: z.record(z.string(), MonthlyTokenUsageSchema).optional(),
  collaboratedNovels: z.array(CollaboratedNovelSchema).optional().default([]),
  pendingInvitations: z.array(PendingInvitationSchema).optional().default([]),
  currentNovelId: z.string().nullable().optional(),
  relationshipsViewMode: z.enum(['list', 'graph']).optional(),
  showMiniMap: z.boolean().optional(),
  displayExampleNovel: z.boolean().optional()
    .describe('If true, the per-language example novel is shown to this user. Defaults to hidden.'),
  themeMode: z.enum(['light', 'dark']).optional()
    .describe('Preferred application color theme. Falls back to the device-local preference when unset.'),
  entityHighlightsEnabled: z.boolean().optional()
    .describe('Whether inline entity highlights are enabled in the editor.'),
});

export type User = z.infer<typeof UserSchema>;
export type TokenUsageEntry = z.infer<typeof TokenUsageEntrySchema>;
export type MonthlyTokenUsage = z.infer<typeof MonthlyTokenUsageSchema>;
export type PendingInvitation = z.infer<typeof PendingInvitationSchema>;
export type CollaboratedNovel = z.infer<typeof CollaboratedNovelSchema>;

export const InvitationDocumentSchema = z.object({
  novelId: z.string().min(1),
  ownerId: z.string().min(1),
  role: z.enum(["collaborator", "reader"]),
  novelTitle: z.string().min(1),
  invitedUid: z.string().optional(),
  invitedEmail: z.string().email().optional(),
  createdAt: z.any().describe("Firestore ServerTimestamp or date string"),
}).refine((data) => (data.invitedEmail !== undefined) !== (data.invitedUid !== undefined), {
  message: "Either invitedEmail or invitedUid must be provided, but not both",
  path: ["invitedEmail", "invitedUid"],
});

export type InvitationDocument = z.infer<typeof InvitationDocumentSchema>;
